import React, { useState } from "react";
import { Modal, DatePicker, Typography, message } from "antd";
import {
  EditOutlined,
  FallOutlined,
  FieldTimeOutlined,
} from "@ant-design/icons";
import { UiButton } from "../../ui";
import ModeCards from "../common/ModeCards";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import { getPharmacyConfigListApi } from "../../../services/pharmacyConfigs.services";
import { getPharmacyPurchaseOrderSuggestionsApi } from "../../../services/pharmacyPurchaseOrders.services";
import { getErrorMessage } from "../../../helpers/error";

const fetchVendors = (text) =>
  getPharmacyConfigListApi(
    "vendors",
    new URLSearchParams({ search: text || "", limit: 50 })
  ).then((res) =>
    (res?.data?.results || []).map((item) => ({ value: item.id, label: item.name }))
  );

const MODES = [
  {
    key: "manual",
    title: "Manual",
    hint: "Start with a blank order and add products yourself.",
    icon: <EditOutlined />,
    color: "#3b6ef6",
    background: "#eaf0ff",
  },
  {
    key: "low_stock",
    title: "Low Stock",
    hint: "Pull in everything running below its reorder level for a vendor.",
    icon: <FallOutlined />,
    color: "#b54708",
    background: "#fffaeb",
  },
  {
    key: "near_expiry",
    title: "Near Expiry",
    hint: "Pull in stock expiring by a chosen date so it can be replaced.",
    icon: <FieldTimeOutlined />,
    color: "#b42318",
    background: "#fef3f2",
  },
];

const first = (...values) => values.find((v) => v !== undefined && v !== null);

let rowSeq = 0;
const toRow = (item) => ({
  key: `po-suggest-${(rowSeq += 1)}`,
  medicine: first(
    item?.medicine,
    item?.medicine_id,
    item?.record_id,
    item?.medicine_record_id,
    item?.id
  ),
  medicine_name: first(item?.medicine_name, item?.name, item?.product_name) || "",
  ordered_qty:
    Number(
      first(
        item?.suggested_qty,
        item?.order_qty,
        item?.ordered_qty,
        item?.quantity,
        item?.qty
      )
    ) || null,
  rate:
    Number(
      first(
        item?.rate,
        item?.purchase_rate,
        item?.last_purchase_rate,
        item?.purchase_price
      )
    ) || null,
  discount: Number(item?.discount) || 0,
  gst_percent:
    Number(first(item?.gst_percent, item?.gst, item?.tax_percent)) || 0,
});

const readSuggestions = (data) => {
  const list = Array.isArray(data)
    ? data
    : data?.results || data?.items || data?.suggestions || data?.data || [];
  return (Array.isArray(list) ? list : []).map(toRow).filter((row) => row.medicine);
};

const RaisePurchaseOrderModal = ({ open, onClose, onStart }) => {
  const [mode, setMode] = useState("manual");
  const [vendor, setVendor] = useState(undefined);
  const [vendorName, setVendorName] = useState("");
  const [expiresBy, setExpiresBy] = useState(null);
  const [loading, setLoading] = useState(false);

  const close = () => {
    setMode("manual");
    setVendor(undefined);
    setVendorName("");
    setExpiresBy(null);
    setLoading(false);
    onClose();
  };

  const handleContinue = () => {
    if (mode === "manual") {
      onStart(null);
      close();
      return;
    }

    if (!vendor) {
      message.warning("Select a vendor first.");
      return;
    }
    if (mode === "near_expiry" && !expiresBy) {
      message.warning("Select an expiry cut-off date.");
      return;
    }

    const params = new URLSearchParams({ type: mode, vendor });
    if (mode === "near_expiry") {
      params.set("expires_by", expiresBy.format("YYYY-MM-DD"));
    }

    setLoading(true);
    getPharmacyPurchaseOrderSuggestionsApi(params)
      .then((res) => {
        const rows = readSuggestions(res?.data);
        if (!rows.length) {
          message.info(
            mode === "low_stock"
              ? "Nothing is below its reorder level for this vendor."
              : "Nothing from this vendor expires by that date."
          );
          return;
        }
        onStart({ vendor, vendorName, rows, source: mode });
        close();
      })
      .catch((err) => message.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      title="Raise Purchase Order"
      width={620}
      destroyOnClose
      footer={[
        <UiButton key="cancel" variant="secondary" onClick={close}>
          Cancel
        </UiButton>,
        <UiButton key="continue" loading={loading} onClick={handleContinue}>
          Continue
        </UiButton>,
      ]}
    >
      <Typography.Text type="secondary">
        How do you want to build this order?
      </Typography.Text>

      <div style={{ marginTop: 12 }}>
        <ModeCards modes={MODES} value={mode} onChange={setMode} />
      </div>

      {mode === "manual" ? null : (
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <Typography.Text style={{ fontSize: 13 }}>Vendor</Typography.Text>
            <div style={{ marginTop: 4 }}>
              <AsyncSearchSelect
                fetcher={fetchVendors}
                loadOnOpen
                placeholder="Search vendor"
                value={vendor}
                onChange={(value, option) => {
                  setVendor(value);
                  setVendorName(option?.label || "");
                }}
              />
            </div>
          </div>

          {mode === "near_expiry" ? (
            <div>
              <Typography.Text style={{ fontSize: 13 }}>Expiring By</Typography.Text>
              <div style={{ marginTop: 4 }}>
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD MMM, YYYY"
                  value={expiresBy}
                  onChange={setExpiresBy}
                />
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
};

export default RaisePurchaseOrderModal;
